import React, {useEffect} from 'react';
import type {WithRouterProps} from 'react-router';

import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';

import {CALCULATOR_NAMES, CalculatorTypeAnnouncer} from './CalculatorTypeAnnouncer';
import {IN_APP_CALCULATOR_FLAG} from './useIsCalculatorAvailable';

/**
 * Layout for the `/calculator` route: while the `in_app_calculator` flag is off
 * (or still loading) it renders nothing and redirects away rather than expose
 * the tools. Gating the parent means every calculator child — including ones
 * added later — is covered without opting in.
 */
export function InAppCalculatorGate({
  children,
  router,
  location
}: React.PropsWithChildren<
  Pick<WithRouterProps, 'router' | 'location'>
>): React.JSX.Element | null {
  const {isFeatureEnabled, isLoading: isFlagLoading} =
    useIsFeatureFlagEnabled(IN_APP_CALCULATOR_FLAG);

  useEffect(() => {
    if (!isFlagLoading && !isFeatureEnabled) {
      router.replace('/home');
    }
  }, [isFlagLoading, isFeatureEnabled, router]);

  if (isFlagLoading || !isFeatureEnabled) {
    return null;
  }

  // The gate stays mounted across /calculator/* pages, so its region is already in place
  // when a type switch changes the page.
  const calculatorType = location.pathname.split('/')[2]?.toLowerCase() ?? '';
  return (
    <>
      {children}
      <CalculatorTypeAnnouncer name={CALCULATOR_NAMES[calculatorType]} />
    </>
  );
}
